type Breakpoint = 'xxs' | 'xs' | 'small' | 'medium';

interface CalculateColumnsPerRowProps {
  currentBreakpoint: Breakpoint | string;
  isSingleColumnLayout: boolean;
  isLargeCardSize: boolean;
}

/**
 * Calculates the number of columns per row based on breakpoint, layout, and card size
 * @param params - The parameters object
 * @param params.currentBreakpoint - The current breakpoint ('xxs', 'xs', 'small', 'medium')
 * @param params.isSingleColumnLayout - Whether the layout is single column (horizontal filters)
 * @param params.isLargeCardSize - Whether large card size is enabled
 * @returns The number of columns per row
 */
export const calculateColumnsPerRow = ({
  currentBreakpoint,
  isSingleColumnLayout,
  isLargeCardSize,
}: CalculateColumnsPerRowProps): number => {
  let numberOfColumnsPerRow: number;

  // Set base columns based on breakpoint
  switch (currentBreakpoint) {
    case 'xxs':
      numberOfColumnsPerRow = 2;
      break;
    case 'xs':
      numberOfColumnsPerRow = 3;
      break;
    case 'small':
      numberOfColumnsPerRow = 4;
      break;
    case 'medium':
      numberOfColumnsPerRow = isSingleColumnLayout ? 6 : 4;
      break;
    default:
      numberOfColumnsPerRow = 4; // fallback for unknown breakpoints
  }

  // Apply large card size adjustments
  if (isLargeCardSize) {
    // For non-horizontal filters, large card size results in 1 column fewer per row at small *and above*
    if (
      !isSingleColumnLayout &&
      ['small', 'medium'].includes(currentBreakpoint)
    ) {
      numberOfColumnsPerRow -= 1;
    }

    // For horizontal filters, large card size results in different adjustments
    if (isSingleColumnLayout) {
      if (currentBreakpoint === 'small') {
        numberOfColumnsPerRow -= 1; // 1 column fewer at small breakpoint
      } else if (currentBreakpoint === 'medium') {
        numberOfColumnsPerRow -= 2; // 2 columns fewer at medium breakpoint
      }
    }
  }

  return numberOfColumnsPerRow;
};
